import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SYSTEM_USER_ID } from '@ast-data/shared';
import { useUserDirectory } from './userDirectory';

/**
 * Resolves the user id an audit column carries (`updatedBy`, a history version's
 * `changedBy`) to a display name — blank when the id is empty or names an
 * account this device has never synced.
 *
 * Every account, not just the active ones: a record keeps naming whoever wrote
 * it, even after that account has been deactivated.
 */
export function useActorLabel(): (userId?: string) => string {
    const intl = useIntl();
    const { users, label: userLabel } = useUserDirectory();
    const userById = useMemo(
        () => new Map(users.map((user) => [user.id, user])),
        [users]
    );

    return useCallback(
        (userId?: string) => {
            if (!userId) {
                return '';
            }
            if (userId === SYSTEM_USER_ID) {
                return intl.formatMessage({ id: 'history.systemUser' });
            }
            const user = userById.get(userId);
            return user ? userLabel(user) : '';
        },
        [userById, userLabel, intl]
    );
}
